(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-bh-subscription-widget]';
  const CART_ADD_FORM_SELECTOR = 'form[action*="/cart/add"]';

  if (window.BHSubscriptionWidgetLoaded) {
    if (typeof window.BHSubscriptionWidgetInit === 'function') {
      window.BHSubscriptionWidgetInit(document);
    }
    return;
  }

  window.BHSubscriptionWidgetLoaded = true;

  class BHSubscriptionWidget {
    constructor(root) {
      this.root = root;
      this.root.dataset.bhInitialised = 'true';

      this.productId = String(root.dataset.productId || '');
      this.currentVariantId = String(root.dataset.variantId || '');
      this.selection = null;

      this.section = root.closest('[id^="shopify-section-"]') || document;
      this.status = root.querySelector('[data-bh-status]');
      this.variantData = this.readVariantData();
      this.variantIds = new Set(Object.keys(this.variantData.variants || {}));

      if (this.section && this.section.classList) {
        this.section.classList.add('bh-product-has-subscriptions');
        this.applyProductAccent();
      }

      this.handleRootChange = this.handleRootChange.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleQuantityAttempt = this.handleQuantityAttempt.bind(this);

      this.bindEvents();
      this.applyVariant(this.currentVariantId, true);
    }

    applyProductAccent() {
      if (!this.section || !this.section.style) {
        return;
      }

      const accent = String(this.root.dataset.bhAccent || '#22a27e');
      const accentRgb = String(this.root.dataset.bhAccentRgb || '34, 162, 126');
      const contrast = String(this.root.dataset.bhAccentContrast || '#04110d');

      this.section.style.setProperty('--bh-product-accent', accent);
      this.section.style.setProperty('--bh-product-accent-rgb', accentRgb);
      this.section.style.setProperty('--bh-product-accent-contrast', contrast);
    }

    readVariantData() {
      const node = this.root.querySelector('[data-bh-variant-data]');

      if (!node) {
        return { variants: {} };
      }

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[Blackhawk subscription widget] Invalid variant data.', error);
        return { variants: {} };
      }
    }

    bindEvents() {
      this.root.addEventListener('change', this.handleRootChange);
      document.addEventListener('variant:changed', this.handleVariantChanged);

      /*
        Capture submit before Minimog serialises FormData. This also covers a
        sticky product form that is inserted after the widget initialises.
      */
      document.addEventListener('submit', this.handleFormSubmit, true);

      /*
        Recharge Quantity Upsells require the quantity attached to the selling
        plan. If a visible theme quantity control tries to change it, restore
        the selected plan quantity immediately.
      */
      document.addEventListener('click', this.handleQuantityAttempt, true);
      document.addEventListener('change', this.handleQuantityAttempt, true);
    }

    handleRootChange(event) {
      const target = event.target;
      const input = target && target.closest
        ? target.closest('[data-bh-option-input]')
        : null;

      if (!input || !this.root.contains(input)) {
        return;
      }

      this.selectInput(input, true);
    }

    handleVariantChanged(event) {
      const variant = event.detail && event.detail.variant;
      const variantId = variant && variant.id ? String(variant.id) : '';

      if (!variantId || !this.variantIds.has(variantId)) {
        return;
      }

      this.applyVariant(variantId, false);
    }

    handleFormSubmit(event) {
      const form = event.target;

      if (!this.isRelevantForm(form)) {
        return;
      }

      this.syncForm(form);
    }

    handleQuantityAttempt(event) {
      if (!this.selection || this.selection.mode !== 'subscription') {
        return;
      }

      const target = event.target;

      if (!target || !target.closest) {
        return;
      }

      const form = target.closest(CART_ADD_FORM_SELECTOR);

      if (!this.isRelevantForm(form)) {
        return;
      }

      const quantityField = target.closest('[name="quantity"]');
      const quantityControl = target.closest(
        '[data-quantity-selector] button, .m-quantity button, quantity-input button, .quantity__button'
      );

      if (!quantityField && !quantityControl) {
        return;
      }

      window.setTimeout(() => this.syncForm(form), 0);
    }

    applyVariant(variantId, keepCurrentType) {
      const variant = this.variantData.variants[String(variantId)];

      if (!variant) {
        return;
      }

      this.currentVariantId = String(variant.id);
      this.root.dataset.variantId = this.currentVariantId;

      const previouslySelected = this.root.querySelector('[data-bh-option-input]:checked');
      const previousCard = previouslySelected
        ? previouslySelected.closest('[data-bh-option]')
        : null;
      const previousMode = previousCard ? previousCard.dataset.purchaseMode : '';
      const previousQuantity = previousCard
        ? Number(previousCard.dataset.quantity || 1)
        : 1;

      const visibleInputs = [];

      this.root.querySelectorAll('[data-bh-plan-card]').forEach((card) => {
        const targetQuantity = Number(card.dataset.planQuantity || 1);
        const plan = (variant.plans || []).find(
          (candidate) => Number(candidate.quantity) === targetQuantity
        );

        if (!plan) {
          card.hidden = true;
          return;
        }

        card.hidden = false;
        card.dataset.quantity = String(plan.quantity);
        card.dataset.sellingPlanId = String(plan.id);

        const input = card.querySelector('[data-bh-plan-input]');

        if (input) {
          input.value = String(plan.id);
          input.disabled = !variant.available;
          visibleInputs.push(input);
        }

        const price = card.querySelector('[data-bh-plan-price]');
        const compare = card.querySelector('[data-bh-plan-compare]');
        const saving = card.querySelector('[data-bh-plan-saving]');
        const discount = card.querySelector('[data-bh-plan-discount]');

        if (price) {
          price.textContent = plan.totalPriceFormatted;
        }

        if (compare) {
          compare.textContent = plan.comparePriceFormatted;
          compare.hidden = !(Number(plan.saving) > 0);
        }

        if (saving) {
          saving.textContent = `Save ${plan.savingFormatted}`;
          saving.hidden = !(Number(plan.saving) > 0);
        }

        if (discount) {
          discount.textContent = `Save ${plan.discountPercent}%`;
          discount.hidden = !(Number(plan.discountPercent) > 0);
        }
      });

      const oneTimeCard = this.root.querySelector('[data-bh-one-time-card]');
      const oneTimeInput = this.root.querySelector('[data-bh-one-time-input]');
      const oneTimePrice = this.root.querySelector('[data-bh-one-time-price]');

      if (oneTimeCard) {
        oneTimeCard.hidden = Boolean(variant.requiresSellingPlan);
      }

      if (oneTimePrice) {
        oneTimePrice.textContent = variant.oneTimePriceFormatted;
      }

      if (oneTimeInput && !variant.requiresSellingPlan) {
        oneTimeInput.disabled = !variant.available;
        visibleInputs.push(oneTimeInput);
      }

      let nextInput = null;

      if (keepCurrentType || previousMode) {
        if (
          previousMode === 'one-time' &&
          oneTimeInput &&
          oneTimeCard &&
          !oneTimeCard.hidden
        ) {
          nextInput = oneTimeInput;
        } else if (previousMode === 'subscription') {
          nextInput = this.root.querySelector(
            `[data-bh-plan-card][data-plan-quantity="${previousQuantity}"]:not([hidden]) [data-bh-plan-input]`
          );
        }
      }

      if (!nextInput) {
        nextInput = this.root.querySelector(
          '[data-bh-plan-card][data-plan-quantity="1"]:not([hidden]) [data-bh-plan-input]'
        );
      }

      if (!nextInput) {
        nextInput = visibleInputs.find((input) => !input.disabled) || visibleInputs[0] || null;
      }

      if (nextInput) {
        this.selectInput(nextInput, false);
      }
    }

    selectInput(input, userInitiated) {
      const card = input.closest('[data-bh-option]');

      if (!card || card.hidden || input.disabled) {
        return;
      }

      this.root.querySelectorAll('[data-bh-option-input]').forEach((radio) => {
        radio.checked = radio === input;
      });

      this.root.querySelectorAll('[data-bh-option]').forEach((option) => {
        const selected = option === card;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      const mode = card.dataset.purchaseMode === 'subscription'
        ? 'subscription'
        : 'one-time';
      const quantity = Math.max(
        1,
        Number.parseInt(card.dataset.quantity || '1', 10) || 1
      );
      const sellingPlanId = mode === 'subscription'
        ? String(card.dataset.sellingPlanId || input.value || '')
        : '';

      this.selection = {
        mode,
        quantity,
        sellingPlanId,
        variantId: this.currentVariantId
      };

      this.root.classList.toggle('is-subscription-selected', mode === 'subscription');
      this.root.classList.toggle('is-one-time-selected', mode === 'one-time');

      this.syncForms();

      if (this.status) {
        if (mode === 'subscription') {
          this.status.textContent = quantity === 3
            ? 'Three month subscription selected.'
            : 'One month subscription selected.';
        } else {
          this.status.textContent = 'One-off purchase selected.';
        }
      }

      this.root.dispatchEvent(new CustomEvent('blackhawk:purchase-option-change', {
        bubbles: true,
        detail: {
          productId: this.productId,
          variantId: this.currentVariantId,
          purchaseMode: mode,
          sellingPlanId: sellingPlanId || null,
          quantity,
          giftIncluded: mode === 'subscription',
          userInitiated: Boolean(userInitiated)
        }
      }));
    }

    isRelevantForm(form) {
      if (!form || !form.matches || !form.matches(CART_ADD_FORM_SELECTOR)) {
        return false;
      }

      const idField = form.querySelector('[name="id"]');

      if (idField && this.variantIds.has(String(idField.value))) {
        return true;
      }

      return Boolean(
        !idField &&
        this.section &&
        this.section.contains &&
        this.section.contains(form)
      );
    }

    getProductForms() {
      return Array.from(document.querySelectorAll(CART_ADD_FORM_SELECTOR)).filter(
        (form) => this.isRelevantForm(form)
      );
    }

    setFormField(form, name, value) {
      let fields = Array.from(form.querySelectorAll(`[name="${name}"]`));

      if (fields.length === 0) {
        const field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        field.dataset.bhOwned = 'true';
        form.appendChild(field);
        fields = [field];
      }

      fields.forEach((field) => {
        field.disabled = false;
        field.value = String(value);
        field.setAttribute('value', String(value));
      });
    }

    syncForm(form) {
      if (!this.selection || !this.isRelevantForm(form)) {
        return;
      }

      const isSubscription = this.selection.mode === 'subscription';

      this.setFormField(
        form,
        'selling_plan',
        isSubscription ? this.selection.sellingPlanId : ''
      );
      this.setFormField(form, 'quantity', this.selection.quantity);

      form.classList.toggle('bh-form--subscription', isSubscription);

      form.querySelectorAll('[name="quantity"]').forEach((field) => {
        if ('readOnly' in field) {
          field.readOnly = isSubscription;
        }

        if (isSubscription) {
          field.setAttribute('aria-readonly', 'true');
        } else {
          field.removeAttribute('aria-readonly');
        }
      });
    }

    syncForms() {
      this.getProductForms().forEach((form) => this.syncForm(form));
    }
  }

  const initAll = (scope = document) => {
    const roots = [];

    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => roots.push(root));
    }

    roots.forEach((root) => {
      if (root.dataset.bhInitialised === 'true') {
        return;
      }

      new BHSubscriptionWidget(root);
    });
  };

  window.BHSubscriptionWidgetInit = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('page:loaded', () => initAll(document));
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
